import { addLogLine } from '../log';
import { VERIFICATION_SERVER_URL, SUPPLIER_REDIRECT_ROUTE } from '../../shared/consts';
import { makeGetRequestHandleResponse } from '../request';
import { SupplierInitializationResponse, SupplierInitializationFunction } from './types';
import { SupplierCode } from './supplierCodes';
import Mdn from '../../shared/mdn';
import { encryptParams } from '../../shared/paramsEncoding';

type NormalizedZumigoInitializationResponse = {
    dateTime: string,
    trxId: string,
    status: string,
    info: string,
    sessionId: string,
}

type zumigoInitializationResponse = {
    dateTime?: string,
    trxId?: string,
    status?: string,
    info?: string,
}

function normalizeZumigoInitializationResponse(supplierInitializationResponse: zumigoInitializationResponse, sessionId: string): NormalizedZumigoInitializationResponse {
    return {
        dateTime: supplierInitializationResponse.dateTime || 'NO_DATETIME_PROVIDED',
        trxId: supplierInitializationResponse.trxId || 'NO_TRXID_PROVIDED',
        status: supplierInitializationResponse.status || 'NO_STATUS_PROVIDED',
        info: supplierInitializationResponse.info || 'NO_INFO_PROVIDED',
        sessionId: sessionId,
    };
}

// The userId and siteId are not sent to the supplier, but they are needed when we send an event before redirecting
export const initializeWithZumigoSupplier: SupplierInitializationFunction = async (userId: string, siteId: string, extVersion: string, consentId: string, consentTimestamp: string, sessionId: string, mdnObj: Mdn) : Promise<SupplierInitializationResponse> => {
    addLogLine(`initializeWithZumigoSupplier with userId: ${userId}, siteId: ${siteId}, extVersion: ${extVersion}, sessionId: ${sessionId}, and mdn: ${mdnObj}`);
    try {
        if (!sessionId) {
            addLogLine('sessionId is not valid: ' + sessionId);
            throw new Error('sessionId is not valid: ' + sessionId);
        }
        if (!mdnObj) {
            addLogLine('mdn is not valid: ' + mdnObj);
            throw new Error('mdn is not valid: ' + mdnObj);
        }
        const validator_url = VERIFICATION_SERVER_URL + `${SUPPLIER_REDIRECT_ROUTE}/${SupplierCode.ZUMIGO}`;
        const encryptedParamsStr = encryptParams({userId, siteId, extVersion, consentId, consentTimestamp, sessionId, mdn: mdnObj.getFullFormattedMdn()});
        if (!encryptedParamsStr) {
            const errMsg = 'Failed to encrypt params';
            addLogLine(errMsg);
            throw new Error(errMsg);
        }
        const encryptedParamQuery = new URLSearchParams({p: encryptedParamsStr});
        const fetchURL = validator_url + '?' + encryptedParamQuery.toString();
        const zumigoInitializationParsedResponse = await makeGetRequestHandleResponse(fetchURL);
        addLogLine('zumigoInitializationParsedResponse: ' + JSON.stringify(zumigoInitializationParsedResponse));
        if (!zumigoInitializationParsedResponse.success) {
            // Code not 200
            addLogLine('Failed to get response from supplier - response error details -  ' + zumigoInitializationParsedResponse.errorResponseStr);
            return {
                success: false,
                sessionId: sessionId,
                errorStr: zumigoInitializationParsedResponse.errorResponseStr,
                supplierResponeObj: {}
            }
        }
        // else - Response 200
        const zumigoInitializationResponseData = zumigoInitializationParsedResponse.successResponseObj;
        const normaliizedInitilizationResponse = normalizeZumigoInitializationResponse(zumigoInitializationResponseData, sessionId);
        addLogLine('normalized response: ' + JSON.stringify(normaliizedInitilizationResponse));
        return {
            success: true,
            sessionId: sessionId,
            supplierResponeObj: normaliizedInitilizationResponse,
        }
    } catch (error) {
        const errorStr = 'Error in zumigo initializeWithZumigoSupplier -  ' + (error as string);
        addLogLine(errorStr);
        return {
            success: false,
            sessionId: sessionId,
            errorStr: errorStr,
            supplierResponeObj: {},
        }
    }
}
